// Signal de módulo (per-tab, en memoria) para SUNSET: el aviso de deprecación de la versión vieja
// (v1) + la encuesta de transición. Mismo patrón que permanenceSurvey.ts (un signal desacoplado del
// ciclo de vida del componente que lo dispara), con un añadido: guarda la `sunset_date` por feature.
//
// POR QUÉ vive aquí y NO en el cache de versión (useFeatureVersion/toEntry): el backend emite
// `sunset_date` en el GET /v1/feature-version SOLO a los comercios que resuelven a CONTROL (v1) de un
// experimento con retiro programado. Es un dato DERIVADO y efímero (se re-hidrata en cada GET); meterlo
// en el cache obligaría a cablearlo por toEntry/optimistic/writeCache/readCache (5+ call-sites,
// propenso a romper el read-path). En su lugar: el GET lo setea aquí, el banner/overlay lo leen vía
// useSyncExternalStore, y se vacía al cambiar de sesión/sucursal (resetTransitionSurveyState, llamado
// desde AuthContext junto a los demás resets del módulo).
//
// Disparo de la encuesta: PASIVO, a lo sumo una vez de por vida por (comercio, feature) con guard
// DURABLE once-ever (passiveSurveyGuard, en localStorage, sobrevive reload/logout/cambio de sucursal)
// — al resolver `sunset_date` el hook pide la encuesta de transición (omitible, fire-and-forget).
// El feedback NUNCA alimenta el KPI/decisión del experimento (viaja como behavior 'usage_feedback',
// igual que la encuesta de permanencia).

import { claimPassiveSurveyShown, __resetPassiveSurveyGuardForTests } from './passiveSurveyGuard';
import { hasOptedOutUsageFeedback } from './preview';

type Listener = () => void;

// sunset_date por feature ('YYYY-MM-DD' o null). null = sin retiro programado (caso normal).
const sunsetByFeature = new Map<string, string | null>();

// true = hay una encuesta de transición pendiente de mostrar para esta feature.
const requested = new Map<string, boolean>();
const listeners = new Map<string, Set<Listener>>();

// Dedup en-memoria del disparador PASIVO, keyed por (comercio, feature): evita tocar localStorage en
// cada resolución dentro del mismo tab. NO es el gate autoritativo (se vacía en
// reload/logout/cambio de sucursal); el once-ever de por vida lo garantiza el guard DURABLE
// (passiveSurveyGuard). Mismo patrón que passiveShownFor de permanence.
const passiveShownFor = new Set<string>();

function getListeners(featureKey: string): Set<Listener> {
  let set = listeners.get(featureKey);
  if (!set) {
    set = new Set();
    listeners.set(featureKey, set);
  }
  return set;
}

function notify(featureKey: string): void {
  getListeners(featureKey).forEach((l) => l());
}

// Suscripción única por feature: cualquier cambio (sunset_date o encuesta pedida) notifica. El
// consumidor combina este subscribe con el getter que necesite (getSunsetDate / isTransitionSurveyRequested).
export function subscribeTransitionSurvey(featureKey: string, listener: Listener): () => void {
  const set = getListeners(featureKey);
  set.add(listener);
  return () => {
    set.delete(listener);
  };
}

// ── sunset_date ──────────────────────────────────────────────────────────────────────────────
export function getSunsetDate(featureKey: string): string | null {
  return sunsetByFeature.get(featureKey) ?? null;
}

// Lo setea el GET /v1/feature-version. Idempotente: no re-notifica si la fecha no cambió.
export function setSunsetDate(featureKey: string, date: string | null): void {
  const prev = sunsetByFeature.get(featureKey) ?? null;
  const nextVal = date ?? null;
  if (prev === nextVal) return;
  sunsetByFeature.set(featureKey, nextVal);
  notify(featureKey);
}

// ── encuesta de transición ───────────────────────────────────────────────────────────────────
export function isTransitionSurveyRequested(featureKey: string): boolean {
  return requested.get(featureKey) === true;
}

// Pide mostrar la encuesta (omitible, fire-and-forget). Idempotente.
export function requestTransitionSurvey(featureKey: string): void {
  if (requested.get(featureKey) === true) return;
  requested.set(featureKey, true);
  notify(featureKey);
}

// El shell lo llama al enviar el motivo, omitir/cerrar la encuesta, u optar por no volver.
export function clearTransitionSurvey(featureKey: string): void {
  if (requested.get(featureKey) !== true) return;
  requested.set(featureKey, false);
  notify(featureKey);
}

// Disparador PASIVO con guard durable once-ever. Devuelve true si DISPARÓ la encuesta en esta
// llamada (primera vez de por vida para ese comercio+feature); false si ya se mostró antes o si el
// operador optó por no volver a preguntar. Sin commerce activo no dispara ni marca el guard. Lo
// invoca el hook al detectar `sunset_date` tras la hidratación.
export function maybeRequestTransitionSurveyForSunset(featureKey: string, commerceId: string | null): boolean {
  if (!commerceId) return false;
  // Opt-out durable per-usuario COMPARTIDO con la encuesta de permanencia (misma llave
  // optOutUsageFeedback): si el operador dijo "No volver a preguntar" en cualquiera de las dos, el
  // disparador pasivo de transición tampoco pide la encuesta. (Nota: GA/evacuado NO se excluyen aquí
  // porque `sunset_date` lo emite el backend SOLO a la cohorte control/v1 en retiro — es
  // mutuamente excluyente con ga_forced por contrato, y no es un estado ETERNO como `adopted`.)
  if (hasOptedOutUsageFeedback(featureKey)) return false;
  const key = `${commerceId}:${featureKey}`;
  // Dedup barato de sesión: si ya se mostró en este tab, ni siquiera tocamos localStorage.
  if (passiveShownFor.has(key)) return false;
  // Gate AUTORITATIVO durable: once-ever de por vida por (comercio, feature).
  if (!claimPassiveSurveyShown('transition', commerceId, featureKey)) {
    passiveShownFor.add(key);
    return false;
  }
  passiveShownFor.add(key);
  requestTransitionSurvey(featureKey);
  return true;
}

// Reset de PRODUCCIÓN: al cambiar de sesión/sucursal ni la fecha de retiro, ni un prompt pendiente,
// ni el dedup en-memoria del comercio anterior deben sobrevivir. NO borra el guard DURABLE
// (passiveSurveyGuard): ese debe sobrevivir el cambio de sesión/sucursal para que la encuesta pasiva
// no reaparezca. Coherente con resetPermanenceSurveyState y el resto de resets del módulo (los llama
// AuthContext en el cambio de sesión/comercio).
export function resetTransitionSurveyState(): void {
  const keys = new Set<string>([...sunsetByFeature.keys(), ...requested.keys()]);
  sunsetByFeature.clear();
  requested.clear();
  passiveShownFor.clear();
  keys.forEach((k) => notify(k));
}

// Solo para tests: limpia el estado de módulo Y el guard durable entre casos (aislamiento).
export function __resetTransitionSurveyForTests(): void {
  sunsetByFeature.clear();
  requested.clear();
  listeners.clear();
  passiveShownFor.clear();
  __resetPassiveSurveyGuardForTests();
}
